import { FlagPriority } from '@generated/graphql';
import { Badge } from '@ovComponents/2-component/badge/badge';
import type {
  ColumnDef,
  DataTableColumnMeta,
} from '@ovComponents/2-component/dataTable';
import {
  badgeSeverityFromPriority,
  getReconFlagPriorityBadgeSx,
} from '@ovComponents/4-module/reconFlags/utils/reconFlags.config';
import type { ReconFlagSeverity } from '@ovComponents/4-module/reconFlags/utils/reconFlags.types';
import type { Theme } from 'hephaestus/core';
import { Typography } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import type { FlagGlossaryRowView } from '../utils/flagGlossary.types';
import {
  getFlagPrioritySortRank,
  getGlossaryThresholdSortValue,
} from '../utils/flagGlossarySort';
export const FLAG_GLOSSARY_COLUMN_IDS = [
  'flagName',
  'description',
  'exampleTrigger',
  'category',
  'actualPriority',
  'actualThreshold',
  'defaultPriority',
  'defaultThreshold',
] as const;

const FLAG_CELL_BADGE_SX = {
  px: 2.5,
  py: 1,
  lineHeight: 1,
  minHeight: 'unset',
  borderRadius: 3,
  padding: '3px 10px',
  fontSize: 11,
  fontWeight: 600,
} as const;

export const buildFlagGlossaryColumns = (
  t: TFunction,
  theme: Theme,
): ColumnDef<FlagGlossaryRowView, unknown>[] => {
  const headDefaults: DataTableColumnMeta = {
    headerLabelVariant: 'bodySmall',
    headerLabelWeight: 'bold',
    headerLabelSx: {
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    headerTextColor: theme.color.onSurfaceVariant,
  };

  const dt = (
    sortable: boolean,
    partial: Partial<DataTableColumnMeta> = {},
  ): { dataTable: DataTableColumnMeta } => ({
    dataTable: {
      ...headDefaults,
      ...(sortable ? {} : { disableDataTableSortHandling: true }),
      ...partial,
    },
  });

  const sortableColumn = (
    partial: ColumnDef<FlagGlossaryRowView, unknown>,
  ): ColumnDef<FlagGlossaryRowView, unknown> => ({
    enableSorting: true,
    meta: dt(true),
    ...partial,
  });

  const readOnlyColumn = (
    partial: ColumnDef<FlagGlossaryRowView, unknown>,
  ): ColumnDef<FlagGlossaryRowView, unknown> => ({
    enableSorting: false,
    meta: dt(false),
    ...partial,
  });

  const renderPriorityBadge = (priority: FlagPriority | null | undefined) => {
    if (priority == null) {
      return (
        <Typography variant='bodySmall' sx={{ color: 'text.secondary' }}>
          —
        </Typography>
      );
    }
    const labelKey =
      priority === FlagPriority.Unknown
        ? 'UNKNOWN'
        : (priority as ReconFlagSeverity);
    const severity = badgeSeverityFromPriority(priority);
    return (
      <Badge
        label={t(`reconFlags.severityLabel.${labelKey}`)}
        color='neutral'
        variant='text'
        sx={{
          ...FLAG_CELL_BADGE_SX,
          ...getReconFlagPriorityBadgeSx(theme, severity),
        }}
      />
    );
  };

  return [
    sortableColumn({
      id: 'flagName',
      accessorKey: 'flagName',
      header: () => t('flagGlossary.table.flagName'),
      cell: ({ row }) => (
        <Typography variant='bodyMedium' sx={{ fontWeight: 600 }} noWrap>
          {row.original.flagName}
        </Typography>
      ),
    }),
    readOnlyColumn({
      id: 'description',
      accessorKey: 'description',
      header: () => t('flagGlossary.table.description'),
      cell: ({ row }) => (
        <Typography
          variant='bodyMedium'
          sx={{ minWidth: 160, fontWeight: 500 }}
        >
          {row.original.description}
        </Typography>
      ),
    }),
    readOnlyColumn({
      id: 'exampleTrigger',
      accessorKey: 'exampleTrigger',
      header: () => t('flagGlossary.table.exampleTrigger'),
      cell: ({ row }) => (
        <Typography
          variant='bodyMedium'
          sx={{ minWidth: 160, fontWeight: 500 }}
        >
          {row.original.exampleTrigger}
        </Typography>
      ),
    }),
    readOnlyColumn({
      id: 'category',
      accessorKey: 'categoryLabel',
      header: () => t('flagGlossary.table.category'),
      cell: ({ row }) => (
        <Badge
          label={row.original.categoryLabel}
          variant='text'
          sx={{
            ...FLAG_CELL_BADGE_SX,
            bgcolor: String(theme.color.neutralHover),
            color: String(theme.color.onSurface),
          }}
        />
      ),
    }),
    sortableColumn({
      id: 'actualPriority',
      accessorFn: (row) => getFlagPrioritySortRank(row.entry.actual.priority),
      header: () => t('flagGlossary.table.actualPriority'),
      cell: ({ row }) =>
        renderPriorityBadge(row.original.entry.actual.priority),
    }),
    sortableColumn({
      id: 'actualThreshold',
      accessorFn: (row) =>
        getGlossaryThresholdSortValue(
          row.entry.actual,
          row.entry.axis,
          row.entry.isAmountThresholdable,
        ),
      header: () => t('flagGlossary.table.actualThreshold'),
      cell: ({ row }) => (
        <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
          {row.original.actualThresholdDisplay}
        </Typography>
      ),
    }),
    sortableColumn({
      id: 'defaultPriority',
      accessorFn: (row) =>
        getFlagPrioritySortRank(row.entry.inherited.priority),
      header: () => t('flagGlossary.table.defaultPriority'),
      cell: ({ row }) =>
        renderPriorityBadge(row.original.entry.inherited.priority),
    }),
    sortableColumn({
      id: 'defaultThreshold',
      accessorFn: (row) =>
        getGlossaryThresholdSortValue(
          row.entry.inherited,
          row.entry.axis,
          row.entry.isAmountThresholdable,
        ),
      header: () => t('flagGlossary.table.defaultThreshold'),
      cell: ({ row }) => (
        <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
          {row.original.defaultThresholdDisplay}
        </Typography>
      ),
    }),
  ];
};
